import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Router, NavigationEnd, ActivatedRoute } from "@angular/router";
import { combineLatest } from "rxjs/observable/combineLatest";
import { ProjectService } from "../project/project.service";
import { SprintService } from "../sprint/sprint.service";
import { RiskIndicatorService } from "../risk-indicator/risk-indicator.service";
import { QuestionnaireService } from "../questionnaire/questionnaire.service";
import { SessionService } from "../session/session.service";

@Injectable()
export class RouteDataService {
  public CurrentProjectShortId: Observable<string>;
  public CurrentProjectName: Observable<string>;
  public CurrentSprintShortId: Observable<string>;
  public CurrentSprintName: Observable<string>;
  public CurrentIndicatorShortId: Observable<string>;
  public CurrentIndicatorName: Observable<string>;
  public CurrentQuestionnaireShortId: Observable<string>;
  public CurrentQuestionnaireName: Observable<string>;
  public CurrentStakeholderIndex: Observable<number>;
  public CurrentStakeholderName: Observable<string>;
  public CurrentNavItem: Observable<string>;
  public ShowNavBar: Observable<boolean>;
  public ShowHeader: Observable<boolean>;
  public ShowNavBarQuestionnaire: Observable<boolean>;
  public ShowHeaderQuestionnaire: Observable<boolean>;
  public Breadcrumbs: Observable<Set<string>>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private projectService: ProjectService,
    private sprintService: SprintService,
    private indicatorService: RiskIndicatorService,
    private questionnaireService: QuestionnaireService
  ) {
    let routeParams = this.router.events
      .filter((event) => event instanceof NavigationEnd)
      .map(() => this.route)
      .startWith(this.route)
      .map((x) => {
        let route = x;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.params;
      })
      .switch()
      .shareReplay(1);

    let routeData = this.router.events
      .filter((event) => event instanceof NavigationEnd)
      .map(() => this.route)
      .startWith(this.route)
      .map((x) => {
        let route = x;
        let routes: Array<ActivatedRoute> = [];
        routes.push(route);
        while (route.firstChild) {
          route = route.firstChild;
          routes.push(route);
        }
        return combineLatest(routes.map((x) => x.data));
      })
      .switch()
      .shareReplay(1);

    this.CurrentProjectShortId = routeParams
      .map((params) => params["id"])
      .distinctUntilChanged();
    this.CurrentProjectName = this.CurrentProjectShortId.map((projectId) => {
      if (!projectId) return Observable.of<string>(null);
      return Observable.fromPromise(
        this.projectService.getProjectByShortId(projectId)
      ).map((x) => x.name);
    }).switch();

    this.CurrentSprintShortId = routeParams
      .map((params) => params["sprintId"])
      .distinctUntilChanged();
    this.CurrentSprintName = this.CurrentSprintShortId.map((sprintId) => {
      if (!sprintId) return Observable.of<string>(null);
      return Observable.fromPromise(
        this.sprintService.getSprintByShortId(sprintId)
      ).map((x) => x.name);
    }).switch();

    this.CurrentIndicatorShortId = routeParams
      .map((params) => params["riskIndicatorId"])
      .distinctUntilChanged();
    this.CurrentIndicatorName = this.CurrentIndicatorShortId.map(
      (riskIndicatorId) => {
        if (!riskIndicatorId) return Observable.of<string>(null);
        return Observable.fromPromise(
          this.indicatorService.getRiskIndicatorByShortId(riskIndicatorId)
        ).map((x) => x.name);
      }
    ).switch();

    this.CurrentQuestionnaireShortId = routeParams
      .map((params) => params["questionnaireId"])
      .distinctUntilChanged();
    this.CurrentQuestionnaireName = combineLatest(
      this.CurrentQuestionnaireShortId,
      this.sessionService.getObservable("questionnaire-token")
    ).switchMap(([questionnaireId, _]) => {
      if (!questionnaireId) return Observable.of<string>(null);
      return this.questionnaireService
        .getQuestionnaireByShortId(questionnaireId)
        .map((x) => x.name)
        .catch((_) => Observable.of(null));
    });

    this.CurrentStakeholderIndex = routeParams
      .map((params) => params["stakeholderIndex"])
      .distinctUntilChanged();
    this.CurrentStakeholderName = combineLatest(
      this.CurrentQuestionnaireShortId,
      this.CurrentStakeholderIndex,
      this.sessionService.getObservable("questionnaire-token")
    ).switchMap(([questionnaireId, stakeholderIndex, _]) => {
      if (!questionnaireId || !stakeholderIndex)
        return Observable.of<string>(null);
      return this.questionnaireService
        .getQuestionnaireByShortId(questionnaireId)
        .map((x) => x.stakeholders[stakeholderIndex].name)
        .catch((_) => Observable.of(null));
    });

    this.ShowNavBar = routeData
      .map(
        (data) =>
          data
            .slice()
            .reverse()
            .map((x) => x["ShowNavBar"])
            .filter((x) => x !== undefined)[0]
      )
      .distinctUntilChanged();
    this.ShowHeader = routeData
      .map(
        (data) =>
          data
            .slice()
            .reverse()
            .map((x) => x["ShowHeader"])
            .filter((x) => x !== undefined)[0]
      )
      .distinctUntilChanged();
    this.ShowNavBarQuestionnaire = routeData
      .map(
        (data) =>
          data
            .slice()
            .reverse()
            .map((x) => x["ShowNavBarQuestionnaire"])
            .filter((x) => x !== undefined)[0]
      )
      .distinctUntilChanged();
    this.ShowHeaderQuestionnaire = routeData
      .map(
        (data) =>
          data
            .slice()
            .reverse()
            .map((x) => x["ShowHeaderQuestionnaire"])
            .filter((x) => x !== undefined)[0]
      )
      .distinctUntilChanged();
    this.CurrentNavItem = routeData
      .map(
        (data) =>
          data
            .slice()
            .reverse()
            .map((x) => x["NavItem"])
            .filter((x) => x !== undefined)[0]
      )
      .distinctUntilChanged();
    this.Breadcrumbs = routeData
      .map(
        (data) =>
          new Set(
            data.map((x) => x["Breadcrumb"]).filter((x) => x !== undefined)
          )
      )
      .distinctUntilChanged();
  }
}



// WEBPACK FOOTER //
// /agir/src/app/services/route-data/route-data.service.ts